"use client"

import { useEffect, useState, type ReactNode } from "react"

/**
 * Mount-gate for `<AnimatePresence>` and other components that internally
 * call `useId()` during render (or otherwise trip Next 16's
 * `cacheComponents: true` "treat as dynamic" rule). Renders `fallback` on
 * the server and during initial hydration; renders `children` after the
 * first `useEffect` tick on the client.
 *
 * # Why this exists
 *
 * Framer-motion's `<AnimatePresence>` wraps every child in a
 * `PresenceChild` that calls `useId()`. Under Next 16 cacheComponents,
 * `useId` is treated as dynamic — anything that depends on it can't be
 * statically prerendered. Without a `<Suspense>` ancestor, the bailout
 * walks up the tree until it hits the root layout's `<Suspense>` and the
 * whole page captures as an empty `<main>` for cold-cache requests
 * (observed: `nav=0 footer=0 h1=0 main=0 bailout=1 text=51` on
 * `/ch/de/membership` cold).
 *
 * Wrapping `<AnimatePresence>` in `<Suspense fallback={null}>` fixes the
 * bailout but emits a `<!--$?-->` placeholder marker inside `<main>`,
 * which the `pre-release: edge cache coverage` validator
 * (`scripts/validation/edge-cache.ts`) classifies as `ppr-deferred` and
 * fails — opposite-direction failure. The Suspense fix is therefore
 * deprecated in this repo. Use this helper instead.
 *
 * # How this fixes it
 *
 * `mounted` is `false` during SSR and during the first client render
 * (hydration consistency). The `useEffect` flips it to `true` AFTER React
 * commits the static markup, at which point we render `children` for real
 * on the client. The result: the AnimatePresence subtree is never present
 * in the prerendered HTML — no bailout, no Suspense placeholder, fully
 * edge-cacheable static shell.
 *
 * # When to use it
 *
 * Wrap the **outermost** `<AnimatePresence>` whose children are rendered
 * during initial render — keyed swap-in-place (filter tabs, step
 * crossfades) or "render overlay A or B based on state" patterns where
 * SSR sees at least one child. Keep the wrapper as tight around the
 * `<AnimatePresence>` as possible — wrapping a whole section would hide
 * its SSR content from bots.
 *
 * # When NOT to use it
 *
 * For `{isOpen && <m.div>…</m.div>}` patterns (modals, dropdowns,
 * accordions opening on click) where `isOpen=false` at SSR, a bare
 * `<AnimatePresence>` is fine — no child means no `PresenceChild` mounts
 * and no bailout. These usages are grandfathered by
 * `scripts/lint-animate-presence.ts` and don't need this wrapper.
 *
 * For SEO-meaningful content that must appear in the static shell, do
 * NOT wrap in `<ClientOnly>` — that defeats SSR. Either avoid
 * `<AnimatePresence>` entirely (CSS transitions instead) or render the
 * content as plain HTML in `fallback` so bots and edge cache see it.
 *
 * # Trade-off
 *
 * Bots that don't run JavaScript won't see `children`. For the
 * marketing-site usage (decorative animations layered atop content
 * that's already in the static shell), this is fine — Googlebot runs JS,
 * and bare HTML crawlers wouldn't have rendered the animations anyway.
 *
 * @example
 * ```tsx
 * import { ClientOnly } from "@/lib/animation/client-only"
 *
 * <ClientOnly>
 *   <AnimatePresence mode="sync">
 *     {state === "a" && <m.div key="a">…</m.div>}
 *     {state === "b" && <m.div key="b">…</m.div>}
 *   </AnimatePresence>
 * </ClientOnly>
 * ```
 */
export function ClientOnly({
  children,
  fallback = null,
}: {
  children: ReactNode
  fallback?: ReactNode
}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  return <>{mounted ? children : fallback}</>
}
